import { FC, ReactNode } from 'react';
import { Field, FieldProps, FieldRenderProps } from 'react-final-form';
import Select from 'react-select';
import classNames from 'classnames';

export interface Option {
  value: string;
  label: string;
}

export interface OptionGroup {
  label: string;
  options: Option[];
}

interface Props extends FieldProps<Option[], FieldRenderProps<Option[]>> {
  name: string;
  label?: ReactNode;
  placeholder?: string;
  options: (Option | OptionGroup)[];
  customOption?: React.ComponentType<any>;
  validate?: (value: Option[]) => string | undefined;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  handleChange?: (value: Option[]) => void;
  isDisabled?: boolean;
  customComponents?: Record<string, React.ComponentType<any>>;
}

export const MultiSelectField: FC<Props> = ({
  name,
  label,
  placeholder = 'Search',
  options,
  customOption,
  closeMenuOnSelect = true,
  hideSelectedOptions = true,
  validate,
  handleChange,
  isDisabled = false,
  customComponents,
  ...fieldProps
}) => {
  return (
    <Field name={name} validate={validate} {...fieldProps}>
      {({ input, meta }) => {
        const hasError = (meta.error || meta.submitError) && meta.touched;

        return (
          <div className={classNames('form-group', hasError ? 'has-danger' : '')}>
            {label && <label htmlFor={name}>{label}</label>}
            <Select
              id={name}
              className={classNames('react-select', hasError ? 'error' : 'success')}
              classNamePrefix="react-select"
              placeholder={placeholder}
              name={name}
              isMulti
              isSearchable
              isDisabled={isDisabled}
              closeMenuOnSelect={closeMenuOnSelect}
              hideSelectedOptions={hideSelectedOptions}
              isClearable={false}
              value={input.value || []}
              onChange={value => {
                handleChange?.(value);
                input.onChange(value);
              }}
              onBlur={input.onBlur}
              onFocus={input.onFocus}
              onKeyDown={e => {
                if (e.key === 'Backspace' && !e.target.value) {
                  e.preventDefault();
                }
              }}
              options={options}
              components={{
                ...(customOption ? { Option: customOption } : {}),
                ...(customComponents ?? {}),
              }}
              styles={fieldProps.styles}
            />
            {hasError && (
              <label htmlFor={name} className="error">
                {meta.error || meta.submitError}
              </label>
            )}
          </div>
        );
      }}
    </Field>
  );
};
